/**
 * BOQ Comment Thread
 * Comment list + input, attachable to document, category, or item.
 */

import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Avatar, Divider, IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { brand } from '../../theme/colors';

export default function BOQCommentThread({ comments = [], onAddComment, loading }) {
  const [content, setContent] = useState('');

  const handleSubmit = () => {
    if (!content.trim()) return;
    onAddComment(content.trim());
    setContent('');
  };

  return (
    <Box>
      {comments.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.4 }} />
          <Typography variant="caption" display="block">No comments yet</Typography>
        </Box>
      )}

      {comments.map((c, idx) => {
        const profile = c.user_profile || {};
        return (
          <Box key={c.id}>
            {idx > 0 && <Divider sx={{ my: 1 }} />}
            <Box sx={{ display: 'flex', gap: 1.5, py: 1 }}>
              <Avatar
                src={profile.profile_image_url}
                sx={{ width: 28, height: 28, bgcolor: brand.primary, fontSize: '0.7rem' }}
              >
                {(profile.first_name || '?')[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {profile.first_name} {profile.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' '}
                    {new Date(c.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.25 }}>{c.content}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <TextField
          size="small"
          placeholder="Add a comment..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmit())}
          fullWidth
          multiline
          maxRows={3}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <IconButton
          onClick={handleSubmit}
          disabled={!content.trim() || loading}
          sx={{ color: brand.primary, alignSelf: 'flex-end' }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
